/**
 * Plain-language diagnostics for JSON that people paste or upload.
 *
 * Browsers word JSON.parse errors differently and rarely say how to fix
 * them, so this module scans the text itself: it finds the exact line and
 * column of the first problem and names the likely cause (a trailing comma,
 * single quotes, a comment, a missing comma...). Validation failures from
 * schemas are turned into "where / what / how to fix" rows the same way.
 */

export interface JsonLocation {
  /** 1-based line. */
  line: number;
  /** 1-based column. */
  column: number;
  /** 0-based character offset. */
  offset: number;
}

export interface JsonSyntaxProblem extends JsonLocation {
  /** What went wrong, in plain words. */
  message: string;
  /** How to fix it. */
  hint: string;
  /** The offending line and its neighbours, for display. */
  excerpt: { line: number; text: string }[];
}

export type JsonParseResult =
  { ok: true; value: unknown } | { ok: false; problem: JsonSyntaxProblem };

export interface ValidationProblem {
  /** Readable location such as "Pages › 2 › Width", or "" for the whole file. */
  where: string;
  /** Raw path segments, for programmatic use. */
  path: (string | number)[];
  message: string;
}

const MAX_EXCERPT = 160;

function locate(text: string, offset: number): JsonLocation {
  let line = 1,
    column = 1;
  for (let i = 0; i < offset && i < text.length; i++) {
    if (text[i] === "\n") {
      line++;
      column = 1;
    } else column++;
  }
  return { line, column, offset };
}

function excerpt(text: string, line: number) {
  const lines = text.split("\n");
  const from = Math.max(1, line - 2),
    to = Math.min(lines.length, line + 1);
  const out: { line: number; text: string }[] = [];
  for (let n = from; n <= to; n++) {
    const raw = lines[n - 1].replace(/\r$/, "");
    out.push({
      line: n,
      text: raw.length > MAX_EXCERPT ? raw.slice(0, MAX_EXCERPT) + "…" : raw,
    });
  }
  return out;
}

class Fault extends Error {
  constructor(
    readonly offset: number,
    message: string,
    readonly hint: string,
  ) {
    super(message);
  }
}

/** A strict JSON scanner that explains the first problem it meets. */
function scan(text: string) {
  let i = 0;
  const ws = () => {
    for (;;) {
      const c = text[i];
      if (c === " " || c === "\t" || c === "\n" || c === "\r") i++;
      else if (c === "/" && (text[i + 1] === "/" || text[i + 1] === "*"))
        throw new Fault(
          i,
          "Comments aren't allowed in JSON.",
          "Delete the comment (the text starting with // or /*).",
        );
      else return;
    }
  };
  const describe = (c: string | undefined) =>
    c === undefined ? "the end of the file" : `“${c}”`;
  const value = (): void => {
    ws();
    const c = text[i];
    if (c === "{") return object();
    if (c === "[") return array();
    if (c === '"') return string();
    if (c === "'")
      throw new Fault(
        i,
        "Text uses single quotes.",
        "JSON needs double quotes: change 'text' to \"text\".",
      );
    if (c === "-" || (c >= "0" && c <= "9")) return number();
    for (const word of ["true", "false", "null"])
      if (text.startsWith(word, i)) {
        i += word.length;
        return;
      }
    if (c === undefined)
      throw new Fault(
        i,
        "The file ends before a value.",
        "Something is missing at the end. Check that every { and [ is closed.",
      );
    const word = /^[A-Za-z_]+/.exec(text.slice(i))?.[0];
    if (
      word &&
      /^(True|False|None|NULL|Null|undefined|NaN|Infinity)$/.test(word)
    )
      throw new Fault(
        i,
        `“${word}” isn't a JSON value.`,
        "Use true, false or null (lower case), or put text in double quotes.",
      );
    if (word)
      throw new Fault(
        i,
        `Text “${word.slice(0, 24)}” is missing its quotes.`,
        'Wrap text values in double quotes, for example "' +
          word.slice(0, 24) +
          '".',
      );
    throw new Fault(
      i,
      `Unexpected ${describe(c)} where a value should be.`,
      "Expected text in double quotes, a number, true, false, null, an object { } or a list [ ].",
    );
  };
  const string = () => {
    const start = i;
    i++;
    for (;;) {
      const c = text[i];
      if (c === undefined)
        throw new Fault(
          start,
          "This text is never closed.",
          "Add the closing double quote.",
        );
      if (c === "\n")
        throw new Fault(
          i,
          "Text runs onto a new line.",
          "Close the quote on the same line, or write the line break as \\n.",
        );
      if (c === "\\") {
        const next = text[i + 1];
        if (next === "u") {
          if (!/^[0-9a-fA-F]{4}$/.test(text.slice(i + 2, i + 6)))
            throw new Fault(
              i,
              "Broken \\u escape.",
              "Use \\u followed by four hex digits, e.g. \\u00e9.",
            );
          i += 6;
          continue;
        }
        if (!next || !'"\\/bfnrt'.includes(next))
          throw new Fault(
            i,
            `“\\${next ?? ""}” isn't a valid escape.`,
            "Write a backslash as \\\\ (common in Windows paths).",
          );
        i += 2;
        continue;
      }
      if (c === '"') {
        i++;
        return;
      }
      if (c < " ")
        throw new Fault(
          i,
          "Text contains an invisible control character.",
          "Delete it or retype the text.",
        );
      i++;
    }
  };
  const number = () => {
    const m = /^-?(0|[1-9]\d*)(\.\d+)?([eE][+-]?\d+)?/.exec(text.slice(i));
    if (!m)
      throw new Fault(
        i,
        "This number is written incorrectly.",
        "Use digits like 12, -3.5 or 1e6, without leading zeros or a trailing dot.",
      );
    i += m[0].length;
    if (/[0-9A-Za-z.]/.test(text[i] ?? ""))
      throw new Fault(
        i,
        "This number is written incorrectly.",
        "Numbers can't have leading zeros, units or a trailing dot. Put values with units in quotes.",
      );
  };
  const array = () => {
    i++;
    ws();
    if (text[i] === "]") {
      i++;
      return;
    }
    for (;;) {
      value();
      ws();
      const c = text[i];
      if (c === ",") {
        const comma = i;
        i++;
        ws();
        if (text[i] === "]")
          throw new Fault(
            comma,
            "There's an extra comma before ].",
            "Delete the comma after the last item in the list.",
          );
        continue;
      }
      if (c === "]") {
        i++;
        return;
      }
      if (c === undefined)
        throw new Fault(
          i,
          "A list is never closed.",
          "Add the missing ] at the end of the list.",
        );
      throw new Fault(
        i,
        "Items in a list need a comma between them.",
        "Add a comma before this item, or close the list with ].",
      );
    }
  };
  const object = () => {
    i++;
    ws();
    if (text[i] === "}") {
      i++;
      return;
    }
    for (;;) {
      ws();
      const c = text[i];
      if (c === "'")
        throw new Fault(
          i,
          "A property name uses single quotes.",
          "Use double quotes around property names.",
        );
      if (c !== '"') {
        if (c === undefined)
          throw new Fault(
            i,
            "An object is never closed.",
            "Add the missing } at the end.",
          );
        if (/[A-Za-z_$]/.test(c))
          throw new Fault(
            i,
            "A property name is missing its quotes.",
            `Wrap it in double quotes, for example "${/^[\w$]+/.exec(text.slice(i))?.[0]}".`,
          );
        throw new Fault(
          i,
          `Unexpected ${describe(c)} where a property name should be.`,
          'Each entry looks like "name": value.',
        );
      }
      string();
      ws();
      if (text[i] !== ":")
        throw new Fault(
          i,
          "A property name isn't followed by a colon.",
          'Write "name": value with a colon between them.',
        );
      i++;
      value();
      ws();
      const next = text[i];
      if (next === ",") {
        const comma = i;
        i++;
        ws();
        if (text[i] === "}")
          throw new Fault(
            comma,
            "There's an extra comma before }.",
            "Delete the comma after the last property.",
          );
        continue;
      }
      if (next === "}") {
        i++;
        return;
      }
      if (next === undefined)
        throw new Fault(
          i,
          "An object is never closed.",
          "Add the missing } at the end.",
        );
      throw new Fault(
        i,
        "Properties need a comma between them.",
        "Add a comma at the end of the previous line.",
      );
    }
  };
  if (text.charCodeAt(0) === 0xfeff) i = 1;
  ws();
  if (i >= text.length)
    throw new Fault(
      i,
      "There's nothing here yet.",
      "Choose a .json file or paste its contents.",
    );
  value();
  ws();
  if (i < text.length)
    throw new Fault(
      i,
      "There's extra content after the end of the JSON.",
      "Remove everything after the final } or ], or check for a stray bracket.",
    );
}

/** Parse JSON, explaining the first problem with its exact position. */
export function parseJson(text: string): JsonParseResult {
  try {
    return { ok: true, value: JSON.parse(text.replace(/^﻿/, "")) };
  } catch (cause) {
    let offset = 0,
      message = "This isn't valid JSON.",
      hint = "Check the highlighted line.";
    try {
      scan(text);
      // The scanner accepted what JSON.parse rejected (very deep nesting).
      message = (cause as Error).message;
    } catch (fault) {
      if (fault instanceof Fault) ({ offset, message, hint } = fault);
      else message = (cause as Error).message;
    }
    const at = locate(text, offset);
    return {
      ok: false,
      problem: { ...at, message, hint, excerpt: excerpt(text, at.line) },
    };
  }
}

/** One-line description, e.g. for alerts and toasts. */
export function describeSyntaxProblem(problem: JsonSyntaxProblem) {
  return `Line ${problem.line}, column ${problem.column}: ${problem.message} ${problem.hint}`;
}

const words = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (c) => c.toUpperCase());

/** "pages.1.width" → "Pages › 2 › Width". Lists count from 1 for people. */
export function humanPath(path: readonly (string | number | symbol)[]) {
  return path
    .map((part) =>
      typeof part === "number" ? String(part + 1) : words(String(part)),
    )
    .join(" › ");
}

type Issue = {
  path?: (string | number)[];
  message?: string;
  code?: string;
  expected?: string;
  received?: string;
  values?: unknown[];
  options?: unknown[];
  minimum?: number | bigint;
  maximum?: number | bigint;
  origin?: string;
  keys?: string[];
  format?: string;
};

const kind = (value: string | undefined) =>
  ({
    string: "text",
    number: "a number",
    int: "a whole number",
    boolean: "true or false",
    object: "an object { }",
    array: "a list [ ]",
    null: "null",
    undefined: "missing",
    nan: "not a number",
    bigint: "a number",
    date: "a date",
  })[value ?? ""] ??
  value ??
  "something else";

function issueMessage(issue: Issue) {
  switch (issue.code) {
    case "invalid_type": {
      const received =
        issue.received ??
        /received (\w+)/.exec(issue.message ?? "")?.[1] ??
        undefined;
      if (received === "undefined") return "This is required but missing.";
      return `Should be ${kind(issue.expected)}${received ? `, but it's ${kind(received)}` : ""}.`;
    }
    case "invalid_value":
    case "invalid_enum_value": {
      const choices = (issue.values ?? issue.options ?? []).map((v) =>
        JSON.stringify(v),
      );
      return choices.length
        ? `Must be one of: ${choices.slice(0, 8).join(", ")}${choices.length > 8 ? "…" : ""}.`
        : "This value isn't allowed here.";
    }
    case "too_small":
      return issue.origin === "array"
        ? `Needs at least ${issue.minimum} item${issue.minimum === 1 ? "" : "s"}.`
        : issue.origin === "string"
          ? Number(issue.minimum) <= 1
            ? "Can't be empty."
            : `Must be at least ${issue.minimum} characters.`
          : `Must be ${issue.minimum} or more.`;
    case "too_big":
      return issue.origin === "array"
        ? `Can have at most ${issue.maximum} items.`
        : issue.origin === "string"
          ? `Must be ${issue.maximum} characters or fewer.`
          : `Must be ${issue.maximum} or less.`;
    case "unrecognized_keys":
      return `Unknown propert${(issue.keys?.length ?? 0) === 1 ? "y" : "ies"}: ${(issue.keys ?? []).join(", ")}. Remove ${(issue.keys?.length ?? 0) === 1 ? "it" : "them"} or check the spelling.`;
    case "invalid_format":
    case "invalid_string":
      return `Isn't a valid ${issue.format ?? "value"}.`;
    case "invalid_union":
      return "Doesn't match any of the accepted shapes.";
    default:
      return (issue.message ?? "This value isn't valid.").replace(
        /^Invalid input:?\s*/i,
        "",
      );
  }
}

function issuesOf(error: unknown): Issue[] | undefined {
  const candidate = error as { issues?: unknown; message?: unknown };
  if (Array.isArray(candidate?.issues)) return candidate.issues as Issue[];
  const text =
    typeof error === "string"
      ? error
      : typeof candidate?.message === "string"
        ? candidate.message
        : "";
  const trimmed = text.trim();
  if (!trimmed.startsWith("[") || !trimmed.endsWith("]")) return undefined;
  try {
    const parsed = JSON.parse(trimmed);
    if (
      Array.isArray(parsed) &&
      parsed.length &&
      parsed.every(
        (item) => item && typeof item === "object" && "message" in item,
      )
    )
      return parsed as Issue[];
  } catch {
    // Not a serialised issue list.
  }
  return undefined;
}

/** Turn a schema error (zod issues, serialised issues, or a plain Error) into rows. */
export function describeValidationError(error: unknown): ValidationProblem[] {
  const issues = issuesOf(error);
  if (issues)
    return issues.slice(0, 50).map((issue) => {
      const path = (issue.path ?? []).filter(
        (part): part is string | number =>
          typeof part === "string" || typeof part === "number",
      );
      return { path, where: humanPath(path), message: issueMessage(issue) };
    });
  return [{ path: [], where: "", message: friendlyMessage(error) }];
}

/** Application error codes people can meet while importing. */
const codes: Record<string, string> = {
  CUSTOMIZATION_SCOPE_MISMATCH:
    "This customization was saved from a different project. Open the project it came from, or choose a customization saved from this one.",
  GOOGLE_SERVICE_KEY_INVALID:
    "That isn't a Google service-account key. Download a JSON key for the service account from Google Cloud (IAM › Service accounts › Keys) and use the whole file.",
  PAYLOAD_TOO_LARGE: "The file is too large to import.",
  FORBIDDEN: "You don't have permission to do that.",
  NOT_FOUND: "That item no longer exists. Refresh and try again.",
  CONFLICT:
    "Someone else changed this while you were working. Refresh to get the latest version, then try again.",
};

/** A readable sentence for any thrown value. Leaves ordinary messages alone. */
export function friendlyMessage(error: unknown): string {
  const issues = issuesOf(error);
  if (issues) {
    const rows = describeValidationError({ issues });
    const first = rows[0];
    const rest =
      rows.length > 1
        ? ` (and ${rows.length - 1} more problem${rows.length > 2 ? "s" : ""})`
        : "";
    return `${first.where ? `${first.where}: ` : ""}${first.message}${rest}`;
  }
  const text =
    typeof error === "string"
      ? error
      : error instanceof Error
        ? error.message
        : String((error as { message?: unknown })?.message ?? error ?? "");
  const code = text.trim();
  if (codes[code]) return codes[code];
  if (/^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+$/.test(code))
    return `${words(code.toLowerCase())}.`;
  if (
    /Unexpected token|in JSON at position|is not valid JSON|JSON\.parse/i.test(
      code,
    )
  )
    return "The file isn't valid JSON. Open it in the importer to see exactly where the problem is.";
  return text || "Something went wrong.";
}

/** Rough classification so toasts can use the right tone and icon. */
export function looksLikeError(message: string) {
  const text = message.trim();
  return (
    !!issuesOf(text) ||
    /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+$/.test(text) ||
    /^(unable|cannot|can't|could not|couldn't|failed|invalid|error|not allowed|no permission|forbidden|unknown|missing|the .* (is|are) (invalid|missing|too large))/i.test(
      text,
    ) ||
    /(must|should) (be|have|belong)|isn't valid|is not valid|too large|exceeds|not found|denied|rejected|mismatch/i.test(
      text,
    )
  );
}
